import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppShell } from "@/components/shell/AppShell";
import { GuestView } from "@/components/call/GuestView";
import { ActivationPage } from "@/pages/ActivationPage";
import { resolveBackendUrl } from "@/lib/api";
import { getToken, setToken, clearTokens, shouldRefresh } from "@/lib/tokenStore";
import { StudioProjector } from "@/components/projector/StudioProjector";
import "@/styles/tokens.css";

// Global fetch interceptor:
//   - Injects Authorization: Bearer <boot|session> on all outgoing requests
//     (skips /.well-known/jwks.json — JWKS bootstrap, no auth needed)
//   - On 401: silently refreshes the boot JWT once and retries the original
//     request once with the new token. If refresh itself 401s, force
//     re-activation. Session JWT expiry is routed to the marketing-site
//     #compare-tiers hash (re-purchase flow for v0.2.0).
//   - Legacy 401 detail === "unlicensed" (or message contains it) still
//     redirects to /activate to preserve backward compatibility with the
//     pre-JWT activation flow.
const originalFetch = window.fetch;
window.fetch = async function (input, init) {
  // Pre-inject Bearer token
  const token = getToken();
  const url = typeof input === "string" ? input : (input instanceof Request ? input.url : "");
  const isJwksBootstrap = url.includes("/.well-known/jwks.json");

  if (token && !isJwksBootstrap) {
    init = init || {};
    init.headers = {
      ...(init.headers || {}),
      "Authorization": `Bearer ${token.boot || token.session || ""}`,
    };
  }

  let response = await originalFetch(input, init);

  // Silent refresh on 401 when a boot JWT is present and ripe
  if (response.status === 401 && token?.boot && shouldRefresh(token)) {
    try {
      const refreshRes = await originalFetch(`${import.meta.env.VITE_API_URL || ""}/api/v1/streampersona/auth/refresh`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token.boot}` },
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        setToken({ ...token, boot: data.token });
        // Retry original request once with the new token
        init = init || {};
        init.headers = { ...(init.headers || {}), "Authorization": `Bearer ${data.token}` };
        return await originalFetch(input, init);
      } else if (refreshRes.status === 401) {
        // Refresh token also expired — force re-activation
        clearTokens();
        if (!window.location.pathname.startsWith("/activate")) {
          window.location.href = "/activate";
        }
      }
    } catch (e) {
      console.error("Token refresh failed:", e);
    }
  }

  // Existing 401 handling (for non-token-related 401s like "unlicensed" and session-token expiry)
  if (response.status === 401) {
    try {
      const clone = response.clone();
      const body = await clone.json();
      if (body.detail === "unlicensed" || (body.message && body.message.toLowerCase().includes("unlicensed"))) {
        if (!window.location.pathname.startsWith("/activate")) {
          window.location.href = "/activate";
        }
      }
      // Session JWT expired — route to re-payment
      if (body.detail === "invalid_token" && getToken()?.type === "session") {
        window.location.hash = "#compare-tiers";
        window.location.href = "/";
      }
    } catch (e) {
      // ignore non-json or parsing errors
    }
  }

  return response;
};

resolveBackendUrl().then(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppShell />} />
          <Route path="/join" element={<GuestView />} />
          <Route path="/activate" element={<ActivationPage />} />
          <Route path="/projector" element={<StudioProjector />} />
        </Routes>
      </BrowserRouter>
    </React.StrictMode>
  );
});
